// src/pages/super-admin/Vendors/VendorDetails.tsx
import { useState } from "react";
import { useParams } from "react-router-dom";
import CreateClinic from "@/features/clinics/pages/CreateClinic";
import VendorClinics from "./VendorClinics";
import EditVendor from "./EditVendor";
import { useGetVendorByIdQuery } from "@/api/vendorApi";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  CreditCard,
  FileCheck,
  Inbox,
  Settings,
  Dna,
  Zap
} from "lucide-react";

export default function VendorDetails() {
  const { vendorId } = useParams<{ vendorId: string }>();
  const [createClinicOpen, setCreateClinicOpen] = useState(false);
  const [editVendorOpen, setEditVendorOpen] = useState(false);

  const vendorIdNumber = vendorId ? parseInt(vendorId, 10) : 0;
  const {
    data: vendor,
    isLoading,
    error,
  } = useGetVendorByIdQuery(vendorIdNumber, {
    skip: !vendorIdNumber,
  });

  if (!vendorId) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <p className="text-red-800 font-bold">Vendor ID not found</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <p className="text-gray-500">Loading vendor details...</p>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <p className="text-red-800 font-bold">Error loading vendor</p>
        <p className="text-red-600 text-sm mt-2">{JSON.stringify(error)}</p>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800";
      case "INACTIVE":
        return "bg-gray-100 text-gray-800";
      case "SUSPENDED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatOperatingHours = (hours: any) => {
    if (!hours) return null;
    const parsedHours = typeof hours === 'string' ? JSON.parse(hours) : hours;
    return Object.entries(parsedHours).map(([day, time]) => (
      <div key={day} className="flex justify-between text-sm">
        <span className="capitalize font-medium">{day}:</span>
        <span className="text-gray-600">{time as string}</span>
      </div>
    ));
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">{vendor.vendorName}</h1>
            <Badge className={getStatusColor(vendor.status)}>
              {vendor.status}
            </Badge>
            {vendor.locationCount !== undefined && (
              <Badge variant="outline">
                {vendor.locationCount} Location{vendor.locationCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
            {vendor.accountNumbers && (
              <div className="mt-2 text-gray-600">
                <span className="mr-2">Account Numbers:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {(() => {
                    try {
                      const accs = JSON.parse(vendor.accountNumbers);
                      return Array.isArray(accs) ? accs.map((acc: string, idx: number) => (
                        <Badge key={idx} variant="secondary" className="font-mono">
                          {acc}
                        </Badge>
                      )) : <span className="font-mono">{vendor.accountNumbers}</span>;
                    } catch (e) {
                      return <span className="font-mono">{vendor.accountNumbers}</span>;
                    }
                  })()}
                </div>
              </div>
            )}
        </div>
        
        <div className="flex gap-2">
          <Dialog open={editVendorOpen} onOpenChange={setEditVendorOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Settings className="w-4 h-4 mr-2" />
                Edit Vendor
              </Button>
            </DialogTrigger>
            <DialogContent className="w-full max-w-4xl">
              <DialogHeader>
                <DialogTitle>Edit Vendor: {vendor.vendorName}</DialogTitle>
              </DialogHeader>
              <EditVendor
                vendor={vendor}
                onClose={() => setEditVendorOpen(false)}
              />
            </DialogContent>
          </Dialog>

          <Dialog open={createClinicOpen} onOpenChange={setCreateClinicOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600">
                <Building2 className="w-4 h-4 mr-2" />
                Add Clinic
              </Button>
            </DialogTrigger>
            <DialogContent className="w-full max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Clinic for {vendor.vendorName}</DialogTitle>
              </DialogHeader>
              <CreateClinic
                vendorId={vendor.id}
                onClose={() => setCreateClinicOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Separator />

      {/* Vendor Information Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Contact Information */}
        <div className="space-y-4 p-4 border rounded-lg">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Contact Information
          </h3>
          <div className="space-y-2 text-sm">
            {vendor.contactPersonName && (
              <p>
                <span className="font-medium">Contact Person:</span>{" "}
                {vendor.contactPersonName}
              </p>
            )}
            <p className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-500" />
              {vendor.contactEmail}
            </p>
            <p className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-500" />
              {vendor.contactPhone}
            </p>
            {vendor.businessAddress && (
              <p className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
                <span>
                  {vendor.businessAddress}
                  {vendor.city && `, ${vendor.city}`}
                  {vendor.state && `, ${vendor.state}`}
                  {vendor.zipCode && ` ${vendor.zipCode}`}
                </span>
              </p>
            )}
          </div>
        </div>

        {/* Service Configuration */}
        <div className="space-y-4 p-4 border rounded-lg">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Service Configuration
          </h3>
          <div className="space-y-3 text-sm">
            {vendor.servicesScope && (
              <div>
                <p className="font-medium mb-1">Services Offered:</p>
                <p className="text-gray-600">{vendor.servicesScope}</p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="font-medium">Appointments:</p>
                <Badge variant="outline" className="mt-1">
                  {vendor.appointmentRequirement === "REQUIRED_ALL"
                    ? "Required"
                    : vendor.appointmentRequirement === "NOT_REQUIRED"
                    ? "Walk-ins"
                    : "Varies"}
                </Badge>
              </div>
              
              <div>
                <p className="font-medium">COC Required:</p>
                <Badge variant="outline" className="mt-1">
                  {vendor.cocRequirement === "REQUIRED_ALL"
                    ? "Required"
                    : vendor.cocRequirement === "NOT_REQUIRED"
                    ? "Not Required"
                    : "Varies"}
                </Badge>
              </div>
            </div>

            <div>
              <p className="font-medium">Pricing Model:</p>
              <Badge variant="outline" className="mt-1">
                {vendor.pricingModel === "UNIFORM"
                  ? "Uniform Pricing"
                  : "Location-Specific"}
              </Badge>
            </div>
          </div>
        </div>

        {/* Operating Hours */}
        {vendor.uniformOperatingHours && (
          <div className="space-y-4 p-4 border rounded-lg">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Uniform Operating Hours
            </h3>
            <div className="space-y-1">
              {formatOperatingHours(vendor.uniformOperatingHours)}
            </div>
          </div>
        )}

        {/* Collection Fee */}
        {vendor.collectionFeeConfiguration && (
          <div className="space-y-4 p-4 border rounded-lg">
             <h3 className="text-lg font-semibold flex items-center gap-2">
               <CreditCard className="w-5 h-5" />
               Collection Fee
             </h3>
             <div className="text-sm">
                 {(() => {
                     try {
                         const config = typeof vendor.collectionFeeConfiguration === 'string'
                             ? JSON.parse(vendor.collectionFeeConfiguration)
                             : vendor.collectionFeeConfiguration;
                         
                         if (config.type === "UNIFORM") {
                             return (
                                 <div>
                                     <p className="font-medium">Type: <Badge variant="outline">Uniform</Badge></p>
                                     <p className="mt-1">Amount: <span className="font-semibold">${config.amount}</span></p>
                                 </div>
                             );
                         } else {
                             return (
                                 <div>
                                     <p className="font-medium">Type: <Badge variant="outline">Location Specific</Badge></p>
                                     <p className="text-gray-500 mt-1">Fee is configured per clinic.</p>
                                 </div>
                             );
                         }
                     } catch (e) {
                         return <p className="text-red-500">Error parsing configuration</p>;
                     }
                 })()}
             </div>
          </div>
        )}

        {/* Forms & Authorization */}
        <div className="space-y-4 p-4 border rounded-lg">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileCheck className="w-5 h-5" />
            Forms & Authorization
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              {vendor.acceptsMedigenixForm ? (
                <>
                  <Badge className="bg-green-100 text-green-800">✓ Accepts</Badge>
                  <span>Medigenix Standard Form</span>
                </>
              ) : (
                <>
                  <Badge className="bg-yellow-100 text-yellow-800">⚠ Custom Required</Badge>
                  <span>Vendor-Specific Form Needed</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Result Delivery */}
        <div className="space-y-4 p-4 border rounded-lg">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Inbox className="w-5 h-5" />
            Result Delivery
          </h3>
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium">Method:</span>{" "}
              <Badge variant="outline">
                {vendor.resultDeliveryMethod === "EMAIL" && "📧 Email"}
                {vendor.resultDeliveryMethod === "FAX" && "📠 Fax"}
                {vendor.resultDeliveryMethod === "VENDOR_PORTAL" && "🔐 Vendor Portal"}
              </Badge>
            </p>
            
            {vendor.resultDeliveryDetails && vendor.resultDeliveryMethod !== "VENDOR_PORTAL" && (
              <p className="text-gray-600">
                {vendor.resultDeliveryMethod === "EMAIL" ? "Email: " : "Fax: "}
                {vendor.resultDeliveryDetails}
              </p>
            )}

            {vendor.resultDeliveryMethod === "VENDOR_PORTAL" && (
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() =>  toast.info("Portal credentials management coming soon!")}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Manage Portal Credentials
              </Button>
            )}
          </div>
        {/* Drug Configuration */}
        {vendor.uniformDrugConfiguration && (
          <div className="space-y-4 p-4 border rounded-lg">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Dna className="w-5 h-5" />
              Uniform Drug Configuration
            </h3>
            <div className="text-sm">
                {(() => {
                    try {
                        const config = JSON.parse(vendor.uniformDrugConfiguration);
                        return (
                            <div className="space-y-2">
                                <p className="font-medium text-gray-700">Panels Offered:</p>
                                <div className="flex flex-wrap gap-1">
                                    {config.map((item: any) => (
                                        <Badge key={item.drugPanelId} variant="secondary" className="text-[10px]">
                                            {item.drugPanelId} - ${item.basePrice}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        );
                    } catch (e) {
                        return <p className="text-red-500">Error parsing configuration</p>;
                    }
                })()}
            </div>
          </div>
        )}

        {/* Margin Configuration */}
        {vendor.marginConfiguration && (
          <div className="space-y-4 p-4 border rounded-lg">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Margin Strategy
            </h3>
            <div className="text-sm">
                {(() => {
                    try {
                        const config = typeof vendor.marginConfiguration === 'string'
                            ? JSON.parse(vendor.marginConfiguration)
                            : vendor.marginConfiguration;
                        
                        return (
                            <div className="space-y-2">
                                <p className="font-medium">Type: <Badge variant="outline">{config.type}</Badge></p>
                                {config.type === "UNIFORM" && (
                                    <div className="mt-2 text-[11px] text-gray-600 bg-orange-50 p-2 rounded border border-orange-100">
                                        ℹ️ Uniform margins are applied to all clinics for this vendor.
                                    </div>
                                )}
                                {config.type === "LOCATION_SPECIFIC" && (
                                    <div className="mt-2 text-[11px] text-gray-500">
                                        Margins are overrideable at each clinic location.
                                    </div>
                                )}
                            </div>
                        );
                    } catch (e) {
                        return <p className="text-red-500">Error parsing configuration</p>;
                    }
                })()}
            </div>
          </div>
        )}
      </div>
    </div>

      <Separator />

      {/* Vendor Clinics */}
      <VendorClinics vendorId={vendor.id} vendorName={vendor.vendorName} />
    </div>
  );
}
